import type { PosterTextData } from '../types/canvas';

export type PosterTextSlot = keyof PosterTextData;

export interface PosterDimensions {
  width: number;
  height: number;
}

export interface PosterRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PosterTextBlock {
  id: string;
  slot: PosterTextSlot;
  x: number;
  y: number;
  width?: number;
  align?: 'left' | 'center' | 'right';
  fontFamily: string;
  fontWeight: number | string;
  fontSize: number;
  lineHeight?: number;
  letterSpacing?: number;
  uppercase?: boolean;
  color: string;
  defaultValue: string;
}

export interface PosterTextBackground {
  id: string;
  top: number;
  width: number;
  height: number;
  align?: 'map-center' | 'absolute';
  left?: number;
  dynamic?: boolean;
  padding?: number;
  minWidth?: number;
  maxWidth?: number;
  radius?: number;
  background?: string;
  slots?: PosterTextSlot[];
}

export interface PosterDecorativeLine {
  id: string;
  left: number;
  top: number;
  width: number;
  strokeWidth: number;
  color?: string;
}

export interface PosterBorderDefinition {
  expandX?: number;
  expandY?: number;
  width: number;
  color: string;
}

export interface PosterStyleDefinition {
  id: string;
  name: string;
  baseSize: PosterDimensions;
  containerBackground?: string;
  mapArea: PosterRect;
  mapBorders?: {
    inner?: PosterBorderDefinition;
    outer?: PosterBorderDefinition;
  };
  textBlocks: PosterTextBlock[];
  textBackgrounds?: PosterTextBackground[];
  decorativeLines?: PosterDecorativeLine[];
  thumbnailUrl?: string;
}

export const DEFAULT_POSTER_STYLE_ID = 'abstrakt';

const BASE_SIZE: PosterDimensions = { width: 500, height: 707 };

const posterStyles: Record<string, PosterStyleDefinition> = {
  minimal: {
    id: 'minimal',
    name: 'Minimal',
    baseSize: BASE_SIZE,
    containerBackground: 'bg-white',
    thumbnailUrl: 'linear-gradient(135deg, #F5F1E8 0%, #E8DCC6 25%, #D4C4A8 50%, #F0E6D2 75%, #F5F1E8 100%)',
    mapArea: {
      x: 31,
      y: 36,
      width: 437,
      height: 638,
    },
    mapBorders: {
      inner: { expandX: 3, expandY: 5, width: 1, color: '#000000' },
      outer: { expandX: 0, expandY: 1, width: 4, color: '#000000' },
    },
    textBackgrounds: [
      {
        id: 'minimal-text-bg',
        top: 491,
        width: 433,
        height: 180,
        align: 'map-center',
        dynamic: true,
        padding: 40,
        minWidth: 200,
        maxWidth: 433,
        radius: 0,
        background: 'linear-gradient(180deg, rgba(232,232,232,0) 0%, #f3f3f3 100%)',
        slots: ['title', 'subtitle'],
      },
    ],
    textBlocks: [
      {
        id: 'minimal-title',
        slot: 'title',
        x: 247.5,
        y: 528,
        width: 387,
        align: 'center',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: 36,
        letterSpacing: 6.12,
        uppercase: true,
        color: '#000000',
        defaultValue: 'PARIS',
      },
      {
        id: 'minimal-subtitle',
        slot: 'subtitle',
        x: 245,
        y: 582,
        width: 342,
        align: 'center',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: 16,
        letterSpacing: 10.72,
        uppercase: true,
        color: '#000000',
        defaultValue: 'FRANCE',
      },
      {
        id: 'minimal-detail',
        slot: 'detail',
        x: 250,
        y: 613,
        width: 342,
        align: 'center',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 300,
        fontSize: 14,
        letterSpacing: 1.12,
        color: '#000000',
        defaultValue: '48.846°N / 2.347°E',
      },
    ],
    decorativeLines: [
      { id: 'minimal-line-left', left: 43, top: 592, width: 31, strokeWidth: 3, color: '#000000' },
      { id: 'minimal-line-right', left: 416, top: 592, width: 31, strokeWidth: 3, color: '#000000' },
    ],
  },
  legenda: {
    id: 'legenda',
    name: 'Legenda',
    baseSize: BASE_SIZE,
    containerBackground: 'bg-white',
    thumbnailUrl: 'linear-gradient(180deg, #F5F5F5 0%, #E0E0E0 30%, #4A90E2 50%, #2E5BBA 70%, #D0D0D0 100%)',
    mapArea: {
      x: 13,
      y: 17,
      width: 472,
      height: 674,
    },
    mapBorders: {
      inner: { expandX: 0, expandY: 0, width: 2, color: '#111111' },
    },
    textBackgrounds: [
      {
        id: 'legenda-text-bg',
        top: 592,
        width: 153,
        height: 80,
        align: 'map-center',
        dynamic: true,
        padding: 32,
        minWidth: 153,
        maxWidth: 443,
        background: '#f9fafb',
        slots: ['title', 'subtitle', 'detail'],
      },
    ],
    textBlocks: [
      {
        id: 'legenda-title',
        slot: 'title',
        x: 249,
        y: 603,
        width: undefined, // Dynamická šířka - bude se přizpůsobovat textu
        align: 'center',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: 16,
        letterSpacing: 3.84,
        uppercase: true,
        color: '#111111',
        defaultValue: 'PARIS',
      },
      {
        id: 'legenda-subtitle',
        slot: 'subtitle',
        x: 249,
        y: 628,
        width: undefined, // Dynamická šířka - bude se přizpůsobovat textu
        align: 'center',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: 11,
        letterSpacing: 2.97,
        uppercase: true,
        color: '#222222',
        defaultValue: 'FRANCE',
      },
      {
        id: 'legenda-detail',
        slot: 'detail',
        x: 249,
        y: 650,
        width: undefined, // Dynamická šířka - bude se přizpůsobovat textu
        align: 'center',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 300,
        fontSize: 9,
        letterSpacing: 1.08,
        color: '#333333',
        defaultValue: '48.846°N / 2.347°E',
      },
    ],
  },
  abstrakt: {
    id: 'abstrakt',
    name: 'Abstrakt',
    baseSize: BASE_SIZE,
    containerBackground: 'bg-white',
    thumbnailUrl: 'linear-gradient(45deg, #2C2C2C 0%, #404040 20%, #FEFDFC 40%, #F9F7F5 60%, #1A1A1A 80%, #2C2C2C 100%)',
    mapArea: {
      x: 21,
      y: 139,
      width: 455,
      height: 542,
    },
    textBlocks: [
      {
        id: 'abstrakt-title',
        slot: 'title',
        x: 22,
        y: 40,
        width: 453,
        align: 'left',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 700,
        fontSize: 36,
        letterSpacing: -1.8,
        color: '#000000',
        defaultValue: 'PARIS',
      },
      {
        id: 'abstrakt-subtitle',
        slot: 'subtitle',
        x: 475,
        y: 61,
        width: 453,
        align: 'right',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: 13,
        letterSpacing: 1.95,
        uppercase: true,
        color: '#000000',
        defaultValue: 'FRANCE',
      },
      {
        id: 'abstrakt-detail',
        slot: 'detail',
        x: 21,
        y: 107,
        width: 454,
        align: 'left',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 300,
        fontSize: 9,
        letterSpacing: 0.18,
        color: '#000000',
        defaultValue: '48.8566°N / 2.3522°E',
      },
    ],
    decorativeLines: [
      { id: 'abstrakt-line', left: 21, top: 94, width: 455, strokeWidth: 2, color: '#000000' },
    ],
  },
  paleta: {
    id: 'paleta',
    name: 'Paleta',
    baseSize: BASE_SIZE,
    containerBackground: 'bg-white',
    thumbnailUrl: 'linear-gradient(90deg, #2D5016 0%, #4A6741 25%, #F5D547 50%, #4A90E2 75%, #1A3A0A 100%)',
    mapArea: {
      x: 13,
      y: 17,
      width: 473,
      height: 589,
    },
    decorativeLines: [
      { id: 'paleta-line', left: 13, top: 610, width: 473, strokeWidth: 4, color: '#000000' },
    ],
    textBlocks: [
      {
        id: 'paleta-title',
        slot: 'title',
        x: 16,
        y: 631,
        width: 130,
        align: 'left',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: 15,
        letterSpacing: 4.5,
        uppercase: true,
        color: '#000000',
        defaultValue: 'PARIS',
      },
      {
        id: 'paleta-subtitle',
        slot: 'subtitle',
        x: 16,
        y: 655,
        width: 130,
        align: 'left',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: 11,
        letterSpacing: 3.08,
        uppercase: true,
        color: '#000000',
        defaultValue: 'FRANCE',
      },
      {
        id: 'paleta-detail',
        slot: 'detail',
        x: 16,
        y: 677,
        width: 130,
        align: 'left',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 300,
        fontSize: 10,
        letterSpacing: 0.6,
        color: '#000000',
        defaultValue: '48.846°N / 2.347°E',
      },
    ],
  },
};

export function getPosterStyleDefinition(styleId?: string): PosterStyleDefinition {
  if (!styleId) {
    return posterStyles[DEFAULT_POSTER_STYLE_ID];
  }
  return posterStyles[styleId] || posterStyles[DEFAULT_POSTER_STYLE_ID];
}

export function listPosterStyleDefinitions(): PosterStyleDefinition[] {
  return Object.values(posterStyles);
}

